import { forwardRef, useCallback, useImperativeHandle, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { PresentUrl, educationFormApi } from "../../../text/apidata.js";
import { store } from "../../../Redux/Store.js";
import { getLoaderAction2 } from "../../../Redux/Loader2.js";
import { responsiveToast } from "../../AppComponent/responsiveToast.js";
import {
  selectEmployeeInformation,
  addEducation,
  removeEducation,
  updateEducationField,
  setEducationImage,
  removeEducationImage,
  setEducationSkipped,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./EducationDetails.module.scss";

const EDUCATION_ORDER = ["10th", "12th", "ITI", "Diploma", "UG", "PG", "OTHER"];

const EducationDetails = forwardRef(function EducationDetails(_, ref) {
  const dispatch = useDispatch();

  const { educationDetails, educationImages, educationSkipped } = useSelector(
    selectEmployeeInformation,
  );

  const selfonboardData = useSelector(
    (state) => state.selfonboardingretriveslice?.Selfonboarding_Data,
  );
  const employeeDetails = useSelector(
    (state) =>
      state.Signature_Employee_RetrieveCall_actions?.Signature_Employee_Datas,
  );

  const skipped = educationSkipped;

  const [errorFields, setErrorFields] = useState({});

  const extractEducationFormId = (d) => {
    if (!d) return null;
    const raw =
      d?.employeetimeline_id?.education_details ||
      d?.employeetimeline_id?.education_details_form ||
      d?.education_details_form?.id ||
      d?.education_details_form ||
      d?.education_details_id ||
      d?.education_details ||
      d?.education_id ||
      d?.educationform_id;

    if (typeof raw === "object" && raw !== null) {
      return raw.id || raw._id || null;
    }
    return raw ? String(raw) : null;
  };

  const fetchedEducationFormRef = useRef(false);

  // GET Education Form API on mount / when navigating back
  useEffect(() => {
    const details = selfonboardData || employeeDetails;
    if (!details) {
      dispatch(getLoaderAction2(false));
      return;
    }

    const educationFormId = extractEducationFormId(details);
    if (!educationFormId) {
      dispatch(getLoaderAction2(false));
      return;
    }

    if (fetchedEducationFormRef.current === educationFormId) {
      dispatch(getLoaderAction2(false));
      return;
    }
    fetchedEducationFormRef.current = educationFormId;

    const sessionToken =
      details?.session_token ||
      details?.token ||
      details?.sessionToken ||
      store?.getState()?.users?.UserData?.session?.token;

    const headers = sessionToken
      ? {
        Authorization: String(sessionToken).startsWith("Bearer ")
          ? sessionToken
          : `Bearer ${sessionToken}`,
      }
      : {};

    dispatch(getLoaderAction2(true));

    axios
      .get(`${educationFormApi}${educationFormId}/`, { headers })
      .then((res) => {
        console.log("Education form GET API response:", res.data);
        const data = res.data?.data || res.data;
        if (data) {
          if (data.not_studied !== undefined) {
            dispatch(setEducationSkipped(Boolean(data.not_studied)));
          }
          const typeConfigs = [
            {
              type: "10th",
              prefix: "sslc",
              imgKey: "sslc_id",
            },
            {
              type: "12th",
              prefix: "hsc",
              imgKey: "hsc_id",
            },
            {
              type: "ITI",
              prefix: "iti",
              imgKey: "iti_id",
            },
            {
              type: "Diploma",
              prefix: "diploma",
              imgKey: "diploma_id",
            },
            {
              type: "UG",
              prefix: "ug",
              imgKey: "ug_id",
            },
            {
              type: "PG",
              prefix: "pg",
              imgKey: "pg_id",
            },
            {
              type: "OTHER",
              prefix: "other",
              altPrefix: "school",
              imgKey: "other_id",
              altImgKey: "school_id",
            },
          ];

          typeConfigs.forEach((cfg) => {
            const schoolName =
              data[`${cfg.prefix}_school_name`] ||
              (cfg.altPrefix ? data[`${cfg.altPrefix}_school_name`] : "") ||
              "";

            const schoolPlace =
              data[`${cfg.prefix}_school_place`] ||
              (cfg.altPrefix ? data[`${cfg.altPrefix}_school_place`] : "") ||
              "";

            const yearOfPassing =
              data[`${cfg.prefix}_year_of_passing`] ||
              (cfg.altPrefix ? data[`${cfg.altPrefix}_year_of_passing`] : "") ||
              "";

            const courseName =
              data[`${cfg.prefix}_course_name`] ||
              (cfg.altPrefix ? data[`${cfg.altPrefix}_course_name`] : "") ||
              "";

            const branchName =
              data[`${cfg.prefix}_branch_name`] ||
              (cfg.altPrefix ? data[`${cfg.altPrefix}_branch_name`] : "") ||
              "";

            const imgVal =
              data[cfg.imgKey] ||
              (cfg.altImgKey ? data[cfg.altImgKey] : "") ||
              "";

            const imgUrl =
              typeof imgVal === "object" && imgVal !== null
                ? imgVal.media_url || imgVal.url
                : typeof imgVal === "string"
                  ? imgVal
                  : "";

            const hasAnyData = Boolean(
              schoolName || schoolPlace || yearOfPassing || courseName || branchName || imgUrl
            );

            if (hasAnyData) {
              const existing = educationDetails.entries.find((e) => e.type === cfg.type);
              const entryId = existing ? existing.id : Date.now() + Math.random();

              if (!existing) {
                dispatch(
                  addEducation({
                    id: entryId,
                    type: cfg.type,
                    school_name: schoolName,
                    school_place: schoolPlace,
                    year_of_passing: yearOfPassing,
                    course_name: courseName,
                    branch_name: branchName,
                    standard: "",
                  })
                );
              } else {
                dispatch(updateEducationField({ id: entryId, name: "school_name", value: schoolName }));
                dispatch(updateEducationField({ id: entryId, name: "school_place", value: schoolPlace }));
                dispatch(updateEducationField({ id: entryId, name: "year_of_passing", value: yearOfPassing }));
                if (courseName) dispatch(updateEducationField({ id: entryId, name: "course_name", value: courseName }));
                if (branchName) dispatch(updateEducationField({ id: entryId, name: "branch_name", value: branchName }));
              }

              if (imgUrl) {
                dispatch(
                  setEducationImage({
                    id: entryId,
                    image: {
                      name: `${cfg.type} Document`,
                      url: imgUrl,
                      s3Url: imgUrl,
                    },
                  })
                );
              }
            }
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching Education form GET API:", err);
      })
      .finally(() => {
        dispatch(getLoaderAction2(false));
      });
  }, [dispatch, selfonboardData, employeeDetails]);

  const clearError = (id, name) => {
    setErrorFields((prev) => {
      const entryErrors = new Set(prev[id] || []);
      entryErrors.delete(name);
      return { ...prev, [id]: entryErrors };
    });
  };

  const hasError = (id, name) => errorFields[id]?.has(name) ?? false;

  const addSection = (type) => {
    if (!type || type === "Select Education") return;

    const exists = educationDetails.entries.find((e) => e.type === type);
    if (exists) return;

    dispatch(
      addEducation({
        id: Date.now(),
        type,
        school_name: "",
        school_place: "",
        year_of_passing: "",
        course_name: "",
        branch_name: "",
        standard: "",
      }),
    );
  };

  const removeSection = (id) => {
    if (educationImages[id]?.url) {
      URL.revokeObjectURL(educationImages[id].url);
    }

    dispatch(removeEducation(id));

    setErrorFields((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const updateField = (id, name, value) => {
    dispatch(updateEducationField({ id, name, value }));
    clearError(id, name);
  };

  const getImageSrc = (img) => {
    if (!img) return "";
    if (typeof img === "string") return img;
    if (typeof img.url === "object" && img.url !== null) {
      return img.url.media_url || img.url.url || "";
    }
    return img.url || img.s3Url || "";
  };

  const handleFile = async (id, file) => {
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type?.toLowerCase())) {
      responsiveToast("Please upload a file in JPG, JPEG, or PNG format", "error");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      responsiveToast("File size must be less than 10 MB", "error");
      return;
    }

    const entry = educationDetails.entries.find((e) => e.id === id);
    const typeMap = {
      "10th": "sslc",
      "12th": "hsc",
      "ITI": "iti",
      "Diploma": "diploma",
      "UG": "ug",
      "PG": "pg",
      "OTHER": "school",
    };
    const folderName = entry ? typeMap[entry.type] || "education" : "education";

    if (educationImages[id]?.url) {
      URL.revokeObjectURL(educationImages[id].url);
    }

    const localUrl = URL.createObjectURL(file);

    dispatch(
      setEducationImage({
        id,
        image: {
          name: file.name,
          file,
          url: localUrl,
          s3Url: "",
        },
      }),
    );

    const details = selfonboardData || employeeDetails;
    const sessionToken =
      details?.session_token ||
      details?.token ||
      details?.sessionToken ||
      store?.getState()?.users?.UserData?.session?.token;

    const headers = sessionToken
      ? {
        Authorization: String(sessionToken).startsWith("Bearer ")
          ? sessionToken
          : `Bearer ${sessionToken}`,
      }
      : {};

    dispatch(getLoaderAction2(true));

    try {
      const randomNumber = Math.floor(Math.random() * 10000000000);
      const fileExt = file.name.split(".").pop() || "jpg";
      const formattedFilename = `onboarding/${folderName}/${folderName}${randomNumber}.${fileExt}`;

      const presignedReqData = {
        multiple_files: [
          {
            filename: formattedFilename,
            file_type: file.type || "image/jpeg",
          },
        ],
      };

      const presignedRes = await axios.post(PresentUrl, presignedReqData, { headers });
      const uploadedUrls = presignedRes.data?.data || presignedRes.data;

      let s3CleanUrl = "";
      if (Array.isArray(uploadedUrls) && uploadedUrls.length > 0) {
        const presignedPutUrl = uploadedUrls[0];
        s3CleanUrl = String(presignedPutUrl).split("?")[0];

        await fetch(presignedPutUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type || "image/jpeg",
            "x-amz-acl": "public-read",
          },
          body: file,
        });
      }

      const finalS3Url = s3CleanUrl || localUrl;

      dispatch(
        setEducationImage({
          id,
          image: {
            name: file.name,
            file,
            url: finalS3Url,
            s3Url: finalS3Url,
          },
        }),
      );
    } catch (error) {
      console.error("Error during education presigned upload:", error);
    } finally {
      dispatch(getLoaderAction2(false));
    }
  };

  const getMissingFields = useCallback(() => {
    if (skipped) return [];

    if (educationDetails.entries.length === 0) {
      return ["At least one education entry"];
    }

    const missing = [];
    const newErrorFields = {};

    educationDetails.entries.forEach((entry) => {
      const entryErrors = new Set();

      if (!String(entry.school_name || "").trim()) {
        missing.push(`${entry.type} Institute Name`);
        entryErrors.add("school_name");
      }

      if (!String(entry.school_place || "").trim()) {
        missing.push(`${entry.type} Place`);
        entryErrors.add("school_place");
      }

      if (!String(entry.year_of_passing || "").trim()) {
        missing.push(`${entry.type} Year of Passing`);
        entryErrors.add("year_of_passing");
      }

      newErrorFields[entry.id] = entryErrors;
    });

    setErrorFields(newErrorFields);

    return missing;
  }, [skipped, educationDetails]);

  useImperativeHandle(
    ref,
    () => ({
      getMissingFields,
      isComplete: () => getMissingFields().length === 0,
    }),
    [getMissingFields],
  );

  const sortedEntries = [...educationDetails.entries].sort(
    (a, b) => EDUCATION_ORDER.indexOf(a.type) - EDUCATION_ORDER.indexOf(b.type),
  );

  const renderFields = (entry) => {
    const is10th12th = entry.type === "10th" || entry.type === "12th";
    const isOther = entry.type === "OTHER";

    return (
      <div key={entry.id} className={styles.section}>
        <div className={styles.sectionHeaderRow}>
          <h3>{entry.type}</h3>
          <button type="button" onClick={() => removeSection(entry.id)}>
            ✕
          </button>
        </div>

        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>
              {entry.type} {is10th12th ? "School Name" : "Institute Name"}
              <b>*</b>
            </span>
            <input
              value={entry.school_name || ""}
              className={
                hasError(entry.id, "school_name") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateField(
                  entry.id,
                  "school_name",
                  e.target.value.toUpperCase(),
                )
              }
            />
            {hasError(entry.id, "school_name") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              {entry.type} Place<b>*</b>
            </span>
            <input
              value={entry.school_place || ""}
              className={
                hasError(entry.id, "school_place") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateField(
                  entry.id,
                  "school_place",
                  e.target.value.toUpperCase(),
                )
              }
            />
            {hasError(entry.id, "school_place") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          {!is10th12th && !isOther && (
            <>
              <label className={styles.field}>
                <span>Course</span>
                <input
                  value={entry.course_name || ""}
                  onChange={(e) =>
                    updateField(
                      entry.id,
                      "course_name",
                      e.target.value.toUpperCase(),
                    )
                  }
                />
              </label>

              <label className={styles.field}>
                <span>Branch</span>
                <input
                  value={entry.branch_name || ""}
                  onChange={(e) =>
                    updateField(
                      entry.id,
                      "branch_name",
                      e.target.value.toUpperCase(),
                    )
                  }
                />
              </label>
            </>
          )}

          {isOther && (
            <label className={styles.field}>
              <span>Qualification</span>
              <input
                value={entry.standard || ""}
                onChange={(e) =>
                  updateField(
                    entry.id,
                    "standard",
                    e.target.value.toUpperCase(),
                  )
                }
              />
            </label>
          )}

          <label className={styles.field}>
            <span>
              Year Of Passing<b>*</b>
            </span>
            <input
              value={entry.year_of_passing || ""}
              inputMode="numeric"
              maxLength={4}
              className={
                hasError(entry.id, "year_of_passing") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateField(
                  entry.id,
                  "year_of_passing",
                  e.target.value.replace(/\D/g, "").slice(0, 4),
                )
              }
            />
            {hasError(entry.id, "year_of_passing") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>
        </div>

        <div className={styles.uploadBlock}>
          <span>File</span>

          <p className={styles.note}>
            Note: Upload the file. Verify that the file is in JPG, JPEG, or PNG
            format and that its size is less than 10 MB.
          </p>

          {educationImages[entry.id] ? (
            <div className={styles.previewRow}>
              {getImageSrc(educationImages[entry.id]) ? (
                <img
                  className={styles.imagePreview}
                  src={getImageSrc(educationImages[entry.id])}
                  alt={`${entry.type} preview`}
                />
              ) : (
                <span>{educationImages[entry.id]?.name || "Uploaded Document"}</span>
              )}
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => dispatch(removeEducationImage(entry.id))}
              >
                Remove
              </button>
            </div>
          ) : (
            <label className={styles.uploadButton}>
              Upload file
              <input
                type="file"
                className={styles.hiddenInput}
                accept="image/png,image/jpeg,image/jpg"
                onChange={(e) => handleFile(entry.id, e.target.files?.[0])}
              />
            </label>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.educationDetails}>
      <header className={styles.sectionHeader}>Education Details</header>

      <div className={styles.content}>
        {/* <div className={styles.skipRow} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={Boolean(skipped)}
              onChange={(e) => {
                const isChecked = e.target.checked;
                dispatch(setEducationSkipped(isChecked));
                if (isChecked) {
                  setErrorFields({});
                }
              }}
            />
            Skip Education Details (Not Studied)
          </label>
        </div> */}

        {skipped ? (
          <p className={styles.skippedNote}>
            Education details skipped (Not Studied). Uncheck the box above to fill in education details, or click Save and Continue to proceed.
          </p>
        ) : (
          <>
            <select
              className={styles.dropdown}
              defaultValue=""
              onChange={(e) => {
                addSection(e.target.value);
                e.target.value = "";
              }}
            >
              <option value="">Select Education</option>
              {EDUCATION_ORDER.map((item) => (
                <option key={item} value={item}>
                  {item.toUpperCase()}
                </option>
              ))}
            </select>

            {sortedEntries.map(renderFields)}
          </>
        )}
      </div>
    </div>
  );
});

export default EducationDetails;
